import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { HighlightStyle } from '@/components/editor/EditorInteractable';

// Define highlight color type
export type HighlightColor = 'blue' | 'green' | 'purple' | 'orange' | 'red';

// Define editor settings type for return values
export interface ProfileEditorSettings {
  highlightStyle: HighlightStyle;
  highlightColor: HighlightColor;
  highlightsEnabled: boolean;
}

// Define the state type
interface EditorSettingsState {
  highlightStyle: HighlightStyle;
  highlightColor: HighlightColor;
  highlightsEnabled: boolean;
  isSaving: boolean;
  error: string | null;
}

// Define initial state
const initialState: EditorSettingsState = {
  highlightStyle: 'border',
  highlightColor: 'blue',
  highlightsEnabled: true,
  isSaving: false,
  error: null,
};

// Create async thunk to save settings to user profile
export const saveEditorSettings = createAsyncThunk<
  ProfileEditorSettings,
  void,
  { state: RootState }
>(
  'editorSettings/save',
  async (_, { getState, rejectWithValue }) => {
    const { auth, editorSettings } = getState();
    const userId = auth.user?.id;
    const permissions = auth.permissions || [];

    // Verify user is authenticated and has editor permission
    if (!userId) {
      return rejectWithValue('User not authenticated');
    }

    // Check if the user has editor permission
    if (!permissions.includes('editor') && !permissions.includes('admin')) {
      return rejectWithValue('User does not have editor permission');
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      // Assert the type of editorSettings if it's being inferred as unknown
      const currentEditorSettings = editorSettings as EditorSettingsState;
      
      // Create a simplified settings object with just the necessary properties
      const settingsToSave = {
        'editorSettings.highlightStyle': currentEditorSettings.highlightStyle,
        'editorSettings.highlightColor': currentEditorSettings.highlightColor,
        'editorSettings.highlightsEnabled': currentEditorSettings.highlightsEnabled,
        updatedAt: serverTimestamp()
      };
      
      // Save settings to Firestore
      await updateDoc(userDocRef, settingsToSave);
      
      // Return the current settings for use in extraReducers
      return {
        highlightStyle: currentEditorSettings.highlightStyle,
        highlightColor: currentEditorSettings.highlightColor,
        highlightsEnabled: currentEditorSettings.highlightsEnabled
      };
    } catch (error: any) {
      console.error('Failed to save editor settings:', error);
      return rejectWithValue(error.message || 'Failed to save settings');
    }
  }
);

// Create the slice
const editorSettingsSlice = createSlice({
  name: 'editorSettings',
  initialState,
  reducers: {
    // Set highlight style
    setHighlightStyle: (state, action: PayloadAction<HighlightStyle>) => {
      state.highlightStyle = action.payload;
    },
    // Set highlight color
    setHighlightColor: (state, action: PayloadAction<HighlightColor>) => {
      state.highlightColor = action.payload;
    },
    // Toggle highlights enabled
    toggleHighlights: (state) => {
      state.highlightsEnabled = !state.highlightsEnabled;
    },
    // Initialize settings from user profile
    initializeFromProfile: (state, action: PayloadAction<{ 
      highlightStyle?: HighlightStyle; 
      highlightColor?: HighlightColor; 
      highlightsEnabled?: boolean 
    }>) => {
      if (action.payload.highlightStyle) {
        state.highlightStyle = action.payload.highlightStyle;
      }
      if (action.payload.highlightColor) {
        state.highlightColor = action.payload.highlightColor;
      }
      if (action.payload.highlightsEnabled !== undefined) {
        state.highlightsEnabled = action.payload.highlightsEnabled;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveEditorSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveEditorSettings.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(saveEditorSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string || 'Failed to save settings';
      });
  },
});

// Export actions
export const {
  setHighlightStyle,
  setHighlightColor,
  toggleHighlights,
  initializeFromProfile
} = editorSettingsSlice.actions;

// Export selectors
export const selectHighlightStyle = (state: RootState) => state.editorSettings.highlightStyle;
export const selectHighlightColor = (state: RootState) => state.editorSettings.highlightColor;
export const selectHighlightsEnabled = (state: RootState) => state.editorSettings.highlightsEnabled;
export const selectEditorSettingsSaving = (state: RootState) => state.editorSettings.isSaving;
export const selectEditorSettingsError = (state: RootState) => state.editorSettings.error;

// Export reducer
export default editorSettingsSlice.reducer; 